// @intent: Zod schemas for the /api/v1/edit-agent/turn request + response contract
// @flow: EditAgentTurnRequestSchema validates POST body; EditAgentTurnResponseSchema validates handler output;
//        StreamEventSchema defines the SSE discriminated union for streaming route (W6.2)
//        consumed by the Next.js API route (server) and future client SDK (browser/worker)
// @stale: when handleTurn adds new output fields (W7+) or request shape changes; when streaming event types expand

import { z } from 'zod/v4';
import { ProjectSchema, PatchOpSchema } from '@mintyads/playable-api';

// ─── Request ──────────────────────────────────────────────────────────────────

export const EditAgentTurnRequestSchema = z.object({
  /** Persisted project id; route loads ProjectBodyV1 server-side. */
  projectId: z.uuid(),
  /** Active persisted chat session for this project/user. */
  sessionId: z.uuid(),
  /** Natural-language instruction from the user */
  userMessage: z.string().min(1).max(4000),
});

export type EditAgentTurnRequest = z.infer<typeof EditAgentTurnRequestSchema>;

// ─── Response sub-schemas ─────────────────────────────────────────────────────

export const GateErrorSchema = z.object({
  stage: z.enum(['schema', 'semantic', 'dryRun']),
  code: z.string(),
  message: z.string(),
  path: z.string().optional(),
  opIndex: z.number().int().optional(),
});

export type EditAgentGateError = z.infer<typeof GateErrorSchema>;

export const UsageSchema = z.object({
  /** Total LLM tokens consumed across all retry attempts */
  totalTokens: z.number().int().nonnegative(),
  /** Number of handleTurn attempts used (1 = first attempt ok, 2 = retry) */
  attempts: z.number().int().positive(),
});

export type EditAgentUsage = z.infer<typeof UsageSchema>;

// ─── Response (discriminated union ok/fail) ───────────────────────────────────

export const EditAgentTurnResponseSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    /** Updated project state after patch applied */
    project: ProjectSchema,
    /** Plain-text reply when the model chose not to call a tool */
    replyText: z.string().optional(),
    usage: UsageSchema,
  }),
  z.object({
    ok: z.literal(false),
    errors: z.array(GateErrorSchema),
    usage: UsageSchema,
  }),
]);

export type EditAgentTurnResponse = z.infer<typeof EditAgentTurnResponseSchema>;

// ─── W6.2 SSE streaming event schema ─────────────────────────────────────────

/**
 * Streaming-specific usage shape: per-direction token counts + cost.
 * Distinct from UsageSchema (which has totalTokens+attempts for the JSON response path).
 * costUSD is backfilled by the route handler after rateCard.calc(); 0 until then.
 */
const StreamUsageSchema = z.object({
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  /** Cost in USD as computed by rateCard.calc(); 0 until backfilled by route */
  costUSD: z.number().nonnegative(),
});

const StreamToolCallSchema = z.object({
  id: z.string(),
  name: z.string(),
  arguments: z.unknown(),
});

const AgentStateSchema = z.enum(['start', 'explored', 'mutating', 'built', 'committed']);

/**
 * Discriminated union of all SSE events emitted by /api/v1/edit-agent/turn.
 *
 * - token:     one content delta from the LLM (stream in order)
 * - phase:     server-side progress phase
 * - retry:     gate rejected attempt 1 or 2; next attempt starting
 * - patches:   accepted PatchOp array to apply to the project
 * - done/ok:   stream complete, all good
 * - done/fail: stream complete, all attempts exhausted
 */
export const StreamEventSchema = z.union([
  // LLM text token — emit as soon as received from adapter
  z.object({
    type: z.literal('token'),
    delta: z.string(),
  }),
  // Server-side progress marker. Clients may ignore this.
  z.object({
    type: z.literal('phase'),
    phase: z.enum(['architect', 'editor', 'verify', 'commit']),
    state: AgentStateSchema.optional(),
  }),
  // Gate/state/tool failure; route is retrying with attempt 2 or 3
  z.object({
    type: z.literal('retry'),
    attempt: z.union([z.literal(2), z.literal(3)]),
    errors: z.array(GateErrorSchema),
    toolCall: StreamToolCallSchema.optional(),
  }),
  // Accepted patch ops — client should apply via useProject.applyPatch (defense in depth)
  z.object({
    type: z.literal('patches'),
    patches: z.array(PatchOpSchema),
  }),
  // Stream complete — success path
  z.object({
    type: z.literal('done'),
    ok: z.literal(true),
    message: z.string(),
    attempts: z.number().int().min(1).max(3),
    usage: StreamUsageSchema,
    commitId: z.string().optional(),
  }),
  // Stream complete — failure path (all attempts rejected or throw)
  z.object({
    type: z.literal('done'),
    ok: z.literal(false),
    message: z.string(),
    errors: z.array(GateErrorSchema),
    attempts: z.number().int().min(1).max(3),
    usage: StreamUsageSchema,
    toolCall: StreamToolCallSchema.optional(),
  }),
]);

export type StreamEvent = z.infer<typeof StreamEventSchema>;
